import { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Building2, Plus, Search, Loader2, Users, Plane, Edit, Eye, Ban, CheckCircle, XCircle, Trash2, Puzzle, FileText } from 'lucide-react';
import { ProposalGeneratorDialog } from '../../components/admin/ProposalGeneratorDialog';
import { TenantBillingCard, usd, fmtDate } from '../../components/admin/TenantBillingCard';
import { CreateDemoButton, DemoResetCard } from '../../components/admin/DemoControls';
import { ManualPaymentsCard } from '../../components/billing/ManualPaymentsCard';
import { toast } from 'sonner';
import { DialogDescription } from '../../components/ui/dialog';

const statusColors = {
    active: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
    trial: 'bg-sky-500/10 text-sky-500 border-sky-500/20',
    suspended: 'bg-red-500/10 text-red-500 border-red-500/20',
    cancelled: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
};

const planBadge = {
    free: 'bg-zinc-500/10 text-zinc-400',
    basic: 'bg-sky-500/10 text-sky-400',
    pro: 'bg-amber-500/10 text-amber-400',
    enterprise: 'bg-violet-500/10 text-violet-400',
};

const statusLabels = { active: 'Activa', trial: 'Prueba', suspended: 'Suspendida', cancelled: 'Cancelada' };

const emptyForm = {
    name: '', slug: '', icao_code: '', iata_code: '', country: '', timezone: 'UTC',
    contact_email: '', contact_phone: '', plan: 'free', tenant_type: 'airline',
    admin_email: '', admin_first_name: '', admin_last_name: '', admin_password: '',
    // Auditoria 3 - Item 3: seleccion de modulos vendibles al crear el tenant
    enabled_modules: [],
    planned_aircraft: '', piloto_hasta: '',
};

export default function AdminTenantsPage() {
    const { apiCall, admin } = useAdminAuth();
    const [billingMap, setBillingMap] = useState({});
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterPlan, setFilterPlan] = useState('all');
    const [filterType, setFilterType] = useState('all');
    const [showCreate, setShowCreate] = useState(false);
    const [showDetail, setShowDetail] = useState(null);
    const [proposalTarget, setProposalTarget] = useState(null);
    const [showEdit, setShowEdit] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [submitting, setSubmitting] = useState(false);
    const [detailData, setDetailData] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    // FASE 6 + Auditoria 9 - Item 5: catalogo de modulos vendibles depende del tenant_type
    const [modulesCatalog, setModulesCatalog] = useState({ core: [], sellable: [] });
    const [modulesDraft, setModulesDraft] = useState([]);
    const [modulesSaving, setModulesSaving] = useState(false);

    // Auditoria 9 - Item 5: recargar el catalogo cuando cambia el tenant en detalle
    // para mostrar los add-ons correctos (airline vs ground_handler).
    useEffect(() => {
        const tt = detailData?.tenant?.tenant_type || 'airline';
        apiCall('get', `/admin/modules/catalog?tenant_type=${tt}`).then(r => setModulesCatalog(r.data)).catch(() => {});
    }, [apiCall, detailData?.tenant?.tenant_type]);

    useEffect(() => {
        if (detailData?.tenant) {
            const cur = detailData.tenant.enabled_modules;
            // null = legacy → todos habilitados
            setModulesDraft(cur === null || cur === undefined ? (modulesCatalog.sellable || []).filter(m => !m.coming_soon).map(m => m.key) : cur);
        }
    }, [detailData, modulesCatalog]);

    const toggleModule = (key) => {
        setModulesDraft(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    };

    const saveModules = async (tenantId) => {
        setModulesSaving(true);
        try {
            await apiCall('put', `/admin/tenants/${tenantId}/modules`, { enabled_modules: modulesDraft });
            toast.success('Módulos actualizados');
            fetchDetail(tenantId);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al guardar módulos');
        } finally {
            setModulesSaving(false);
        }
    };

    const fetchTenants = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (search) params.append('search', search);
            if (filterStatus !== 'all') params.append('status', filterStatus);
            if (filterPlan !== 'all') params.append('plan', filterPlan);
            if (filterType !== 'all') params.append('tenant_type', filterType);
            const res = await apiCall('get', `/admin/tenants?${params.toString()}`);
            setTenants(res.data);
            apiCall('get', '/admin/billing/overview').then(r => setBillingMap(Object.fromEntries(r.data.tenants.map(t => [t.id, t])))).catch(() => {});
        } catch (e) {
            console.error(e);
            toast.error('No se pudieron cargar las empresas');
        } finally {
            setLoading(false);
        }
    }, [apiCall, search, filterStatus, filterPlan, filterType]);

    useEffect(() => { fetchTenants(); }, [fetchTenants]);

    const handleCreate = async () => {
        setSubmitting(true);
        try {
            await apiCall('post', '/admin/tenants', {
                ...form,
                planned_aircraft: form.planned_aircraft === '' ? null : Number(form.planned_aircraft),
                piloto_hasta: form.piloto_hasta || null,
            });
            toast.success('Aerolínea creada exitosamente');
            setShowCreate(false);
            setForm(emptyForm);
            fetchTenants();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al crear aerolínea');
        } finally {
            setSubmitting(false);
        }
    };

    const handleStatusChange = async (tenantId, newStatus) => {
        try {
            await apiCall('patch', `/admin/tenants/${tenantId}/status?status=${newStatus}`);
            toast.success(`Estado actualizado a ${statusLabels[newStatus]}`);
            fetchTenants();
            if (showDetail) fetchDetail(tenantId);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al cambiar estado');
        }
    };

    const handlePlanChange = async (tenantId, newPlan) => {
        try {
            const r = await apiCall('patch', `/admin/tenants/${tenantId}/plan?plan=${newPlan}`);
            toast.success(`Plan actualizado a ${r.data?.billing?.plan_name || newPlan}`);
            if (r.data?.warning) toast.warning(r.data.warning, { duration: 8000 });
            fetchTenants();
            if (showDetail) fetchDetail(tenantId);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al cambiar plan');
        }
    };

    const handleEdit = async () => {
        setSubmitting(true);
        try {
            const { name, contact_email, contact_phone, country, timezone } = showEdit;
            await apiCall('put', `/admin/tenants/${showEdit.id}`, { name, contact_email, contact_phone, country, timezone });
            toast.success('Aerolínea actualizada');
            setShowEdit(null);
            fetchTenants();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al actualizar');
        } finally {
            setSubmitting(false);
        }
    };

    const fetchDetail = async (id) => {
        setDetailLoading(true);
        try {
            const res = await apiCall('get', `/admin/tenants/${id}`);
            setDetailData(res.data);
        } catch (e) {
            toast.error('Error al cargar detalles');
        } finally {
            setDetailLoading(false);
        }
    };

    const openDetail = (tenant) => {
        setShowDetail(tenant);
        fetchDetail(tenant.id);
    };

    const handleDeleteTenant = async () => {
        if (!deleteConfirm) return;
        try {
            await apiCall('delete', `/admin/tenants/${deleteConfirm.id}`);
            toast.success('Empresa eliminada permanentemente');
            setDeleteConfirm(null);
            fetchTenants();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al eliminar');
        }
    };

    const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-4xl font-bold tracking-tight uppercase font-['Barlow_Condensed']" data-testid="admin-tenants-title">
                        Empresas
                    </h1>
                    <p className="text-muted-foreground mt-1">Gestion de aerolineas y empresas de Ground Handling</p>
                </div>
                <div className="flex gap-2">
                    <CreateDemoButton apiCall={apiCall} onDone={fetchTenants} exists={tenants.some(t => t.is_demo)} />
                    <Button onClick={() => setShowCreate(true)} className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider text-xs" data-testid="create-tenant-btn">
                        <Plus className="w-4 h-4 mr-2" /> Nueva Empresa
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder="Buscar por nombre, ICAO..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 input-aviation"
                        data-testid="tenants-search"
                    />
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-[160px]" data-testid="filter-status">
                        <SelectValue placeholder="Estado" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="active">Activas</SelectItem>
                        <SelectItem value="trial">Prueba</SelectItem>
                        <SelectItem value="suspended">Suspendidas</SelectItem>
                        <SelectItem value="cancelled">Canceladas</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={filterPlan} onValueChange={setFilterPlan}>
                    <SelectTrigger className="w-[160px]" data-testid="filter-plan">
                        <SelectValue placeholder="Plan" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="free">Free</SelectItem>
                        <SelectItem value="basic">Basic</SelectItem>
                        <SelectItem value="pro">Pro</SelectItem>
                        <SelectItem value="enterprise">Enterprise</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="w-[160px]" data-testid="filter-type">
                        <SelectValue placeholder="Tipo" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="airline">Aerolineas</SelectItem>
                        <SelectItem value="ground_handler">Ground Handling</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Table */}
            {loading ? (
                <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
                </div>
            ) : (
                <Card className="card-aviation overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="table-aviation" data-testid="tenants-table">
                            <thead>
                                <tr>
                                    <th>Empresa</th>
                                    <th>Tipo</th>
                                    <th>ICAO/IATA</th>
                                    <th>Pais</th>
                                    <th>Plan</th>
                                    <th>Estado</th>
                                    <th>Usuarios</th>
                                    <th>Mensual</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tenants.map((t) => (
                                    <tr key={t.id} data-testid={`tenant-row-${t.id}`}>
                                        <td className="font-medium">{t.name}{t.is_demo && <Badge variant="outline" className="ml-2 text-sky-400 border-sky-500/40" data-testid={`tenant-demo-badge-${t.id}`}>Demo</Badge>}</td>
                                        <td>
                                            <Badge className={t.tenant_type === 'ground_handler' ? 'bg-violet-500/10 text-violet-400' : 'bg-cyan-500/10 text-cyan-400'} variant="outline">
                                                {t.tenant_type === 'ground_handler' ? 'GH' : 'Airline'}
                                            </Badge>
                                        </td>
                                        <td className="font-mono text-xs">{t.icao_code}{t.iata_code ? ` / ${t.iata_code}` : ''}</td>
                                        <td className="text-sm">{t.country}</td>
                                        <td><Badge className={planBadge[t.plan] || ''}>{t.plan}</Badge></td>
                                        <td><Badge className={statusColors[t.status] || ''} variant="outline">{statusLabels[t.status] || t.status}</Badge></td>
                                        <td className="font-mono text-center">{t.users_count}</td>
                                        <td className="text-xs" data-testid={`tenant-monthly-${t.id}`}>
                                            {billingMap[t.id] ? (
                                                <div>
                                                    <span className="font-mono">{usd(billingMap[t.id].amount_due_monthly)}</span>
                                                    {billingMap[t.id].pilot_active && <div className="text-sky-600 dark:text-sky-400 font-semibold text-[11px]" data-testid={`tenant-pilot-${t.id}`}>Piloto hasta {fmtDate(billingMap[t.id].pilot_until)}</div>}
                                                    {billingMap[t.id].out_of_range && <div className="text-amber-600 dark:text-amber-400 font-semibold text-[11px]">Fuera de rango</div>}
                                                </div>
                                            ) : <span className="text-muted-foreground">—</span>}
                                        </td>
                                        <td>
                                            <div className="flex items-center gap-1">
                                                <Button variant="ghost" size="sm" onClick={() => openDetail(t)} data-testid={`view-tenant-${t.id}`}>
                                                    <Eye className="w-4 h-4" />
                                                </Button>
                                                <Button variant="ghost" size="sm" onClick={() => setShowEdit({ ...t })} data-testid={`edit-tenant-${t.id}`}>
                                                    <Edit className="w-4 h-4" />
                                                </Button>
                                                {t.status === 'active' || t.status === 'trial' ? (
                                                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" onClick={() => handleStatusChange(t.id, 'suspended')}>
                                                        <Ban className="w-4 h-4" />
                                                    </Button>
                                                ) : t.status === 'suspended' ? (
                                                    <>
                                                    <Button variant="ghost" size="sm" className="text-emerald-500 hover:text-emerald-600" onClick={() => handleStatusChange(t.id, 'active')}>
                                                        <CheckCircle className="w-4 h-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" onClick={() => setDeleteConfirm(t)} data-testid={`delete-tenant-${t.id}`}>
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                    </>
                                                ) : null}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {tenants.length === 0 && (
                                    <tr>
                                        <td colSpan={8} className="text-center py-12 text-muted-foreground">
                                            <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                            <p>No se encontraron empresas</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            {/* Create Dialog */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase tracking-tight">
                            Nueva Empresa
                        </DialogTitle>
                        <DialogDescription>Registra una nueva aerolinea o compania de Ground Handling con su administrador inicial.</DialogDescription>
                    </DialogHeader>
                    <Tabs defaultValue="company" className="w-full">
                        <TabsList className="grid w-full grid-cols-3">
                            <TabsTrigger value="company">Datos de Empresa</TabsTrigger>
                            <TabsTrigger value="admin">Administrador</TabsTrigger>
                            <TabsTrigger value="modules" data-testid="create-tenant-tab-modules">Módulos</TabsTrigger>
                        </TabsList>
                        <TabsContent value="company" className="space-y-4 mt-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2 col-span-2">
                                    <Label>Tipo de Empresa</Label>
                                    <Select value={form.tenant_type} onValueChange={(v) => updateForm('tenant_type', v)}>
                                        <SelectTrigger data-testid="create-tenant-type"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="airline">Aerolinea</SelectItem>
                                            <SelectItem value="ground_handler">Ground Handling</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2 col-span-2">
                                    <Label>Nombre</Label>
                                    <Input value={form.name} onChange={(e) => updateForm('name', e.target.value)} placeholder={form.tenant_type === 'ground_handler' ? 'ServiGround S.A.' : 'Avianca'} data-testid="create-tenant-name" className="input-aviation" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Slug</Label>
                                    <Input value={form.slug} onChange={(e) => updateForm('slug', e.target.value.toLowerCase().replace(/\s/g, '-'))} placeholder={form.tenant_type === 'ground_handler' ? 'serviground' : 'avianca'} data-testid="create-tenant-slug" className="input-aviation font-mono" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Pais</Label>
                                    <Input value={form.country} onChange={(e) => updateForm('country', e.target.value)} placeholder="Guatemala" data-testid="create-tenant-country" className="input-aviation" />
                                </div>
                                {form.tenant_type === 'airline' && (
                                    <>
                                        <div className="space-y-2">
                                            <Label>Codigo ICAO</Label>
                                            <Input value={form.icao_code} onChange={(e) => updateForm('icao_code', e.target.value.toUpperCase())} placeholder="AVA" maxLength={4} data-testid="create-tenant-icao" className="input-aviation font-mono" />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Codigo IATA</Label>
                                            <Input value={form.iata_code} onChange={(e) => updateForm('iata_code', e.target.value.toUpperCase())} placeholder="AV" maxLength={2} data-testid="create-tenant-iata" className="input-aviation font-mono" />
                                        </div>
                                    </>
                                )}
                                <div className="space-y-2">
                                    <Label>Email de Contacto</Label>
                                    <Input type="email" value={form.contact_email} onChange={(e) => updateForm('contact_email', e.target.value)} placeholder="ops@avianca.com" data-testid="create-tenant-email" className="input-aviation" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Teléfono</Label>
                                    <Input value={form.contact_phone} onChange={(e) => updateForm('contact_phone', e.target.value)} placeholder="+57 1 234 5678" data-testid="create-tenant-phone" className="input-aviation" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Zona Horaria</Label>
                                    <Input value={form.timezone} onChange={(e) => updateForm('timezone', e.target.value)} placeholder="America/Bogota" data-testid="create-tenant-tz" className="input-aviation font-mono" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Plan</Label>
                                    <Select value={form.plan} onValueChange={(v) => updateForm('plan', v)}>
                                        <SelectTrigger data-testid="create-tenant-plan"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {form.tenant_type === 'ground_handler' ? (
                                                <>
                                                    <SelectItem value="gh_starter">GH Starter - $199/mes</SelectItem>
                                                    <SelectItem value="gh_pro">GH Pro - $499/mes</SelectItem>
                                                    <SelectItem value="gh_enterprise">GH Enterprise - $999/mes</SelectItem>
                                                </>
                                            ) : (
                                                <>
                                                    <SelectItem value="free">Free Trial - sin costo (hasta 2 aeronaves)</SelectItem>
                                                    <SelectItem value="basic">Starter - $390/aeronave (1-5)</SelectItem>
                                                    <SelectItem value="pro">Professional - $320/aeronave (6-15)</SelectItem>
                                                    <SelectItem value="enterprise">Enterprise - $260/aeronave (16+)</SelectItem>
                                                </>
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>
                                {form.tenant_type !== 'ground_handler' && (
                                    <>
                                        <div className="space-y-2">
                                            <Label>Número de aeronaves</Label>
                                            <Input type="number" min="0" value={form.planned_aircraft} onChange={(e) => updateForm('planned_aircraft', e.target.value)} placeholder="Ej. 4" data-testid="create-tenant-planned-aircraft" className="input-aviation font-mono" />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Fin del piloto (opcional)</Label>
                                            <Input type="date" value={form.piloto_hasta} onChange={(e) => updateForm('piloto_hasta', e.target.value)} data-testid="create-tenant-piloto-hasta" className="input-aviation font-mono" />
                                        </div>
                                    </>
                                )}
                            </div>
                        </TabsContent>
                        <TabsContent value="admin" className="space-y-4 mt-4">
                            <p className="text-sm text-muted-foreground">
                                Se creara un usuario {form.tenant_type === 'ground_handler' ? 'supervisor' : 'administrador'} para esta empresa
                            </p>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Nombre</Label>
                                    <Input value={form.admin_first_name} onChange={(e) => updateForm('admin_first_name', e.target.value)} placeholder="Juan" data-testid="create-admin-firstname" className="input-aviation" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Apellido</Label>
                                    <Input value={form.admin_last_name} onChange={(e) => updateForm('admin_last_name', e.target.value)} placeholder="Pérez" data-testid="create-admin-lastname" className="input-aviation" />
                                </div>
                                <div className="space-y-2 col-span-2">
                                    <Label>Email del Administrador</Label>
                                    <Input type="email" value={form.admin_email} onChange={(e) => updateForm('admin_email', e.target.value)} placeholder="admin@avianca.com" data-testid="create-admin-email" className="input-aviation" />
                                </div>
                                <div className="space-y-2 col-span-2">
                                    <Label>Contraseña (opcional)</Label>
                                    <Input type="password" value={form.admin_password} onChange={(e) => updateForm('admin_password', e.target.value)} placeholder="Vacía: el administrador la elige desde el correo" data-testid="create-admin-password" className="input-aviation" />
                                </div>
                            </div>
                        </TabsContent>
                        {/* Auditoria 3 - Item 3: pestana Modulos para elegir enabled_modules al crear */}
                        <TabsContent value="modules" className="space-y-4 mt-4">
                            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-200">
                                <p className="font-bold mb-1">Plan vs Módulos — son independientes.</p>
                                <p>
                                    El <strong>Plan</strong> define el precio por aeronave facturable y su rango de aeronaves, más los límites de usuarios y vuelos por mes.
                                    Los <strong>Módulos</strong> definen qué partes del sistema puede usar (Tripulación, Mantenimiento, IA, etc.).
                                    Un tenant puede tener plan Basic pero acceso a Tripulación por separado, o plan Enterprise sin IA.
                                </p>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Módulos habilitados desde el inicio</Label>
                                {(modulesCatalog.sellable || []).map(m => {
                                    const checked = (form.enabled_modules || []).includes(m.key);
                                    return (
                                        <label key={m.key} className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded hover:bg-muted/30" data-testid={`create-module-${m.key}`}>
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                disabled={m.coming_soon}
                                                onChange={() => {
                                                    const cur = form.enabled_modules || [];
                                                    updateForm('enabled_modules', checked ? cur.filter(k => k !== m.key) : [...cur, m.key]);
                                                }}
                                                className="mt-1"
                                            />
                                            <div className="flex-1">
                                                <div className="font-medium">{m.label}{m.coming_soon && <span className="ml-2 text-[10px] font-bold uppercase text-amber-500">Próximamente</span>}</div>
                                                <div className="text-xs text-muted-foreground">{m.description}</div>
                                                {m.base_price_usd_monthly && !m.coming_soon ? (
                                                    <div className="text-[10px] text-amber-400 mt-0.5">
                                                        ${m.base_price_usd_monthly}/mes · ${Number(m.base_price_usd_yearly).toLocaleString('en-US')}/año
                                                    </div>
                                                ) : null}
                                            </div>
                                        </label>
                                    );
                                })}
                                {(!modulesCatalog.sellable || modulesCatalog.sellable.length === 0) && (
                                    <p className="text-xs text-muted-foreground">Catálogo de módulos no disponible. Se aplicarán defaults.</p>
                                )}
                            </div>
                        </TabsContent>
                    </Tabs>
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
                        <Button onClick={handleCreate} disabled={submitting} className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider text-xs" data-testid="submit-create-tenant">
                            {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                            Crear Empresa
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Detail Dialog */}
            <Dialog open={!!showDetail} onOpenChange={() => { setShowDetail(null); setDetailData(null); }}>
                <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase tracking-tight flex items-center gap-2">
                            <Building2 className="w-6 h-6 text-amber-500" />
                            {showDetail?.name}
                        </DialogTitle>
                        <DialogDescription>Detalle del tenant: plan, uso, modulos habilitados, staff y actividad.</DialogDescription>
                        <div className="flex justify-end -mt-1">
                            <Button size="sm" variant="outline" onClick={() => setProposalTarget(showDetail)} data-testid={`generate-proposal-tenant-${showDetail?.id}`}>
                                <FileText className="w-3.5 h-3.5 mr-1.5" /> Generar propuesta
                            </Button>
                        </div>
                    </DialogHeader>
                    {detailLoading ? (
                        <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>
                    ) : detailData ? (
                        <div className="space-y-6">
                            <DemoResetCard tenant={detailData.tenant} apiCall={apiCall} onDone={fetchTenants} />
                            {/* Tenant info */}
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div><span className="text-muted-foreground">ICAO:</span> <span className="font-mono font-bold ml-1">{detailData.tenant?.icao_code}</span></div>
                                <div><span className="text-muted-foreground">IATA:</span> <span className="font-mono font-bold ml-1">{detailData.tenant?.iata_code || '-'}</span></div>
                                <div><span className="text-muted-foreground">País:</span> <span className="ml-1">{detailData.tenant?.country}</span></div>
                                <div><span className="text-muted-foreground">Zona Horaria:</span> <span className="font-mono ml-1">{detailData.tenant?.timezone}</span></div>
                                <div><span className="text-muted-foreground">Email:</span> <span className="ml-1">{detailData.tenant?.contact_email}</span></div>
                                <div><span className="text-muted-foreground">Teléfono:</span> <span className="ml-1">{detailData.tenant?.contact_phone || '-'}</span></div>
                            </div>

                            {/* Status & Plan */}
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-muted-foreground">Estado:</span>
                                    <Badge className={statusColors[detailData.tenant?.status] || ''} variant="outline">
                                        {statusLabels[detailData.tenant?.status]}
                                    </Badge>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-muted-foreground">Plan:</span>
                                    <Select key={detailData.tenant?.plan} defaultValue={detailData.tenant?.plan} onValueChange={(v) => handlePlanChange(showDetail.id, v)}>
                                        <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="detail-plan-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="free">Free</SelectItem>
                                            <SelectItem value="basic">Starter</SelectItem>
                                            <SelectItem value="pro">Professional</SelectItem>
                                            <SelectItem value="enterprise">Enterprise</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Plan Limits */}
                            {detailData.plan_details && (
                                <Card className="bg-muted/30">
                                    <CardContent className="p-4">
                                        <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-3">Límites del Plan</p>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                                            <div><span className="text-muted-foreground">Aeronaves:</span> <span className="font-mono font-bold ml-1">{detailData.plan_details.min_aircraft ?? 0}–{detailData.plan_details.max_aircraft ?? '∞'}</span></div>
                                            <div><span className="text-muted-foreground">Usuarios:</span> <span className="font-mono font-bold ml-1">{detailData.plan_details.max_users === -1 ? 'Ilimitado' : detailData.plan_details.max_users}</span></div>
                                            <div><span className="text-muted-foreground">Vuelos/mes:</span> <span className="font-mono font-bold ml-1">{detailData.plan_details.max_flights_monthly === -1 ? 'Ilimitado' : detailData.plan_details.max_flights_monthly}</span></div>
                                            <div><span className="text-muted-foreground">Precio:</span> <span className="font-mono font-bold ml-1">{detailData.plan_details.price_per_aircraft !== undefined ? `$${detailData.plan_details.price_per_aircraft}/aeronave` : `$${detailData.plan_details.price_monthly}/mes`}</span></div>
                                        </div>
                                        {detailData.billing?.pilot_active && <p className="text-[11px] text-sky-400 mt-2">Límites de usuarios y vuelos desactivados durante el piloto.</p>}
                                    </CardContent>
                                </Card>
                            )}

                            {detailData.tenant?.tenant_type !== 'ground_handler' && (
                                <TenantBillingCard detail={detailData} apiCall={apiCall} editable={admin?.role === 'superadmin'}
                                    onSaved={() => { fetchDetail(showDetail.id); fetchTenants(); }} />
                            )}
                            {admin?.role === 'superadmin' && detailData.tenant?.id && (
                                <ManualPaymentsCard tenantId={detailData.tenant.id} suggestedAmount={detailData.billing?.total_monthly} apiCall={apiCall} />
                            )}

                            {/* FASE 6: Modulos habilitados */}
                            <Card className="bg-muted/30">
                                <CardContent className="p-4">
                                    <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-3 flex items-center gap-2">
                                        <Puzzle className="w-4 h-4" /> Modulos Vendibles
                                    </p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                                        {(modulesCatalog.sellable || []).map(m => (
                                            <label key={m.key} className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded hover:bg-background/50" data-testid={`module-toggle-${m.key}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={modulesDraft.includes(m.key)}
                                                    disabled={m.coming_soon}
                                                    onChange={() => toggleModule(m.key)}
                                                    className="mt-1"
                                                />
                                                <div>
                                                    <div className="font-medium">{m.label}{m.coming_soon && <span className="ml-2 text-[10px] font-bold uppercase text-amber-500" data-testid={`module-soon-${m.key}`}>Próximamente</span>}</div>
                                                    <div className="text-xs text-muted-foreground">{m.description}</div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                    <div className="flex justify-end">
                                        <Button size="sm" onClick={() => saveModules(showDetail.id)} disabled={modulesSaving} data-testid="save-modules-btn">
                                            {modulesSaving && <Loader2 className="w-3 h-3 mr-2 animate-spin" />}
                                            Guardar modulos
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Users */}
                            <div>
                                <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-2 flex items-center gap-2">
                                    <Users className="w-4 h-4" /> Usuarios ({detailData.users?.length || 0})
                                </p>
                                <div className="space-y-1">
                                    {(detailData.users || []).map((u) => (
                                        <div key={u.id} className="flex items-center justify-between text-sm py-1.5 px-2 rounded hover:bg-muted/30">
                                            <div>
                                                <span className="font-medium">{u.first_name} {u.last_name}</span>
                                                <span className="text-muted-foreground ml-2 text-xs">{u.email}</span>
                                            </div>
                                            <Badge variant="outline" className="text-xs">{u.role}</Badge>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Status actions */}
                            <div className="flex gap-2 pt-2 border-t border-border/50">
                                {(detailData.tenant?.status === 'active' || detailData.tenant?.status === 'trial') && (
                                    <Button variant="outline" className="text-red-500 border-red-500/30 hover:bg-red-500/10" onClick={() => handleStatusChange(showDetail.id, 'suspended')}>
                                        <Ban className="w-4 h-4 mr-2" /> Suspender
                                    </Button>
                                )}
                                {detailData.tenant?.status === 'suspended' && (
                                    <>
                                        <Button variant="outline" className="text-emerald-500 border-emerald-500/30 hover:bg-emerald-500/10" onClick={() => handleStatusChange(showDetail.id, 'active')}>
                                            <CheckCircle className="w-4 h-4 mr-2" /> Reactivar
                                        </Button>
                                        <Button variant="outline" className="text-red-500 border-red-500/30 hover:bg-red-500/10" onClick={() => handleStatusChange(showDetail.id, 'cancelled')}>
                                            <XCircle className="w-4 h-4 mr-2" /> Cancelar
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={!!showEdit} onOpenChange={() => setShowEdit(null)}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase tracking-tight">
                            Editar Aerolínea
                        </DialogTitle>
                    </DialogHeader>
                    {showEdit && (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label>Nombre</Label>
                                <Input value={showEdit.name} onChange={(e) => setShowEdit({ ...showEdit, name: e.target.value })} className="input-aviation" data-testid="edit-tenant-name" />
                            </div>
                            <div className="space-y-2">
                                <Label>Email de Contacto</Label>
                                <Input value={showEdit.contact_email || ''} onChange={(e) => setShowEdit({ ...showEdit, contact_email: e.target.value })} className="input-aviation" data-testid="edit-tenant-email" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>País</Label>
                                    <Input value={showEdit.country} onChange={(e) => setShowEdit({ ...showEdit, country: e.target.value })} className="input-aviation" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Teléfono</Label>
                                    <Input value={showEdit.contact_phone || ''} onChange={(e) => setShowEdit({ ...showEdit, contact_phone: e.target.value })} className="input-aviation" />
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowEdit(null)}>Cancelar</Button>
                        <Button onClick={handleEdit} disabled={submitting} className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider text-xs" data-testid="submit-edit-tenant">
                            {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                            Guardar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Tenant Confirmation */}
            <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
                <DialogContent data-testid="delete-tenant-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-red-500 font-['Barlow_Condensed'] text-xl uppercase">Eliminar Empresa</DialogTitle>
                        <DialogDescription>Esta accion es permanente y no se puede deshacer.</DialogDescription>
                    </DialogHeader>
                    {deleteConfirm && (
                        <div className="space-y-3">
                            <div className="p-3 bg-red-500/10 rounded-lg text-sm">
                                <p className="font-bold">{deleteConfirm.name}</p>
                                <p className="text-muted-foreground text-xs">{deleteConfirm.icao_code || deleteConfirm.slug} - {deleteConfirm.country}</p>
                            </div>
                            <div className="p-3 bg-amber-500/10 rounded-lg text-xs text-amber-300">
                                <p className="font-bold">Politica de eliminacion:</p>
                                <p>Solo se pueden eliminar empresas suspendidas por mas de 12 meses. Se eliminaran permanentemente todos los datos: usuarios, aeronaves, vuelos, mantenimiento, etc.</p>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
                        <Button variant="destructive" onClick={handleDeleteTenant} data-testid="confirm-delete-tenant">
                            <Trash2 className="w-4 h-4 mr-1" />Eliminar Permanentemente
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ProposalGeneratorDialog
                open={!!proposalTarget}
                onOpenChange={(v) => !v && setProposalTarget(null)}
                target={proposalTarget}
                targetType="tenant"
                apiCall={apiCall}
            />
        </div>
    );
}
